// src/components/eventos/views/EventoRevisaoPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';

/* ---- Helpers ---- */
const formatarData = (dataString) => {
    if (!dataString) return 'Data não informada';
    try {
        const [ano, mes, dia] = dataString.split('T')[0].split('-');
        return `${dia}/${mes}/${ano}`;
    } catch { return 'Data inválida'; }
};

const formatarDataCompleta = (dataString) => {
    if (!dataString) return 'Data não informada';
    try {
        const [ano, mes, dia] = dataString.split('T')[0].split('-').map(Number);
        const meses = ['janeiro','fevereiro','março','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro'];
        const diasSemana = ['domingo','segunda-feira','terça-feira','quarta-feira','quinta-feira','sexta-feira','sábado'];
        const data = new Date(ano, mes - 1, dia);
        return `${diasSemana[data.getDay()]}, ${dia} de ${meses[mes - 1]} de ${ano}`;
    } catch { return formatarData(dataString); }
};

const formatarTempo = (minutos) => {
    const h = Math.floor(Math.abs(minutos) / 60);
    const m = Math.abs(minutos) % 60;
    return h > 0 ? `${h}h ${m}min` : `${m}min`;
};

/* ---- SVG Icons ---- */
const ArrowLeftIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6" />
    </svg>
);
const CalendarIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);
const ClockIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
);
const CheckIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);
const PlayIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
);
const ListIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
        <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
);
const LogoutIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
    </svg>
);
const ChevronIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);
const AlertIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
);

const EventoRevisaoPage = ({ supabase, session, clienteData, onLogout }) => {
    const navigate = useNavigate();
    const { eventoId } = useParams();
    const toast = useToast();

    const [evento, setEvento] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [processando, setProcessando] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    const nomeUsuario = session?.user?.user_metadata?.nome || 'Usuário';
    const iniciais = nomeUsuario.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();

    const handleLogout = async () => { try { await onLogout(); } catch (e) { console.error(e); } };

    useEffect(() => {
        const fetchEvento = async () => {
            setIsLoading(true);
            try {
                const clienteId = session.user.user_metadata?.cliente_id;
                let query = supabase.from('eventos').select('*').eq('id', eventoId);
                if (clienteId) query = query.eq('cliente_id', clienteId);
                const { data, error } = await query.single();
                if (error) throw error;
                setEvento(data);
            } catch (error) {
                console.error('Erro ao buscar evento:', error);
                toast({ title: 'Erro ao carregar evento', description: error.message, status: 'error', duration: 5000, isClosable: true });
                navigate('/eventos/diretor');
            } finally {
                setIsLoading(false);
            }
        };
        if (eventoId && supabase && session) fetchEvento();
    }, [eventoId, supabase, session, navigate, toast]);

    const iniciarEvento = async () => {
        if (!evento) return;
        setProcessando(true);
        try {
            const horaAtual = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
            const tarefasAtualizadas = evento.tarefas.map((t, idx) => ({
                ...t,
                status: idx === 0 ? 'em_andamento' : 'pendente',
                horaInicioReal: idx === 0 ? horaAtual : null,
                horaFimReal: null,
            }));
            const { error } = await supabase.from('eventos').update({
                status: 'em_andamento', hora_inicio_real: horaAtual, tarefas: tarefasAtualizadas,
            }).eq('id', evento.id);
            if (error) throw error;
            toast({ title: 'Evento iniciado', description: `${evento.titulo} está em andamento`, status: 'success', duration: 3000, isClosable: true });
            navigate('/eventos/diretor');
        } catch (error) {
            console.error('Erro ao iniciar evento:', error);
            toast({ title: 'Erro ao iniciar evento', description: error.message, status: 'error', duration: 5000, isClosable: true });
        } finally {
            setProcessando(false);
        }
    };

    React.useEffect(() => {
        if (!menuOpen) return;
        const close = () => setMenuOpen(false);
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, [menuOpen]);

    const calcularInfo = () => {
        if (!evento) return null;
        const tarefas = evento.tarefas || [];
        const tempoProgramadoTotal = tarefas.reduce((acc, t) => acc + (t.duracao || 0), 0);
        return {
            totalTarefas: tarefas.length,
            tempoProgramadoTotal,
            primeiraHora: tarefas[0]?.horaInicio || '—',
            ultimaHora: tarefas[tarefas.length - 1]?.horaFim || '—',
        };
    };

    const info = calcularInfo();

    /* ---- Loading ---- */
    if (isLoading) {
        return (
            <>
                <style>{`@keyframes erp-spin { to { transform: rotate(360deg); } }`}</style>
                <div style={{ minHeight: '100vh', background: '#f0faf6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', -apple-system, sans-serif" }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', border: '3px solid rgba(44,203,144,0.2)', borderTopColor: '#2CCB90', animation: 'erp-spin 0.8s linear infinite' }} />
                </div>
            </>
        );
    }

    /* ---- Not found ---- */
    if (!evento) {
        return (
            <div style={{ minHeight: '100vh', background: '#f0faf6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', -apple-system, sans-serif", padding: '24px' }}>
                <div style={{ maxWidth: '480px', width: '100%', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.18)', borderRadius: '16px', padding: '32px', textAlign: 'center' }}>
                    <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(239,68,68,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: '#ef4444' }}>
                        <AlertIcon />
                    </div>
                    <h2 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 700, color: '#ef4444' }}>Evento não encontrado</h2>
                    <p style={{ margin: '0 0 24px', fontSize: '14px', color: '#64748b' }}>O evento solicitado não existe ou você não tem permissão para visualizá-lo.</p>
                    <button onClick={() => navigate('/eventos/diretor')} style={{ padding: '10px 24px', borderRadius: '10px', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#1e293b', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                        Voltar para Diretor
                    </button>
                </div>
            </div>
        );
    }

    const STATS = [
        { label: 'Tarefas Programadas', value: info.totalTarefas, sub: 'Todas aguardando início', color: '#2563eb', bg: 'rgba(37,99,235,0.06)', border: 'rgba(37,99,235,0.15)' },
        { label: 'Tempo Total', value: formatarTempo(info.tempoProgramadoTotal), sub: 'Duração estimada', color: '#2CCB90', bg: 'rgba(44,203,144,0.06)', border: 'rgba(44,203,144,0.15)' },
        { label: 'Status', value: 'Aguardando', sub: 'Pronto para iniciar', color: '#ea580c', bg: 'rgba(234,88,12,0.06)', border: 'rgba(234,88,12,0.15)' },
    ];

    return (
        <>
            <style>{`
                @keyframes erp-spin { to { transform: rotate(360deg); } }

                .erp-menu-item { display:flex; align-items:center; gap:10px; width:100%; padding:10px 14px; border-radius:10px; font-size:13px; font-weight:500; color:#64748b; background:none; border:none; cursor:pointer; font-family:inherit; transition:all 0.15s; text-align:left; }
                .erp-menu-item:hover { background:#f8fafc; color:#1e293b; }
                .erp-menu-item.danger { color:#ef4444; }
                .erp-menu-item.danger:hover { background:rgba(239,68,68,0.06); }

                .erp-task-card { background:#ffffff; border:1px solid #e2e8f0; border-radius:14px; padding:20px; transition:all 0.2s; box-shadow:0 1px 3px rgba(0,0,0,0.05); }
                .erp-task-card:hover { background:#f8fafc; border-color:rgba(139,92,246,0.2); box-shadow:0 4px 12px rgba(0,0,0,0.08); }

                .erp-stats-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:16px; }
                @media (max-width:768px) { .erp-stats-grid { grid-template-columns:1fr; } }

                .erp-info-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:20px; }
                @media (max-width:640px) { .erp-info-grid { grid-template-columns:1fr; } }

                @media (max-width:640px) { .erp-nav-name { display:none; } }
            `}</style>

            <div style={{ minHeight: '100vh', background: '#f0faf6', fontFamily: "'Inter', -apple-system, sans-serif" }}>

                {/* NAVBAR */}
                <header style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', borderBottom: '1px solid #e2e8f0' }}>
                    <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', background: '#ffffff', borderRadius: '10px', padding: '6px 14px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' }}>
                                <img src="/logo-horizontal.png" alt="Evlo" style={{ height: '26px', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
                            </div>
                            <button
                                onClick={() => navigate('/eventos/diretor')}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '10px', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f0fdf8'}
                                onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}
                            >
                                <ArrowLeftIcon /> Voltar
                            </button>
                            {clienteData && (
                                <span style={{ padding: '4px 12px', borderRadius: '100px', background: 'rgba(44,203,144,0.1)', border: '1px solid rgba(44,203,144,0.2)', color: '#2CCB90', fontSize: '11px', fontWeight: 700 }}>
                                    {clienteData.nome}
                                </span>
                            )}
                        </div>

                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
                                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 12px', borderRadius: '12px', background: menuOpen ? '#f8fafc' : '#ffffff', border: '1px solid #e2e8f0', cursor: 'pointer', transition: 'all 0.15s', fontFamily: 'inherit' }}
                            >
                                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, #2CCB90, #2DB8E1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                                    {iniciais}
                                </div>
                                <span className="erp-nav-name" style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>{nomeUsuario}</span>
                                <span style={{ color: '#94a3b8', transition: 'transform 0.2s', transform: menuOpen ? 'rotate(180deg)' : 'none' }}><ChevronIcon /></span>
                            </button>
                            {menuOpen && (
                                <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, minWidth: '190px', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px', boxShadow: '0 16px 48px rgba(0,0,0,0.12)', padding: '8px', zIndex: 100 }}>
                                    <button className="erp-menu-item" onClick={() => { navigate('/perfil'); setMenuOpen(false); }}>Meu Perfil</button>
                                    <div style={{ height: '1px', background: '#e2e8f0', margin: '6px 0' }} />
                                    <button className="erp-menu-item danger" onClick={handleLogout}><LogoutIcon /> Sair</button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                {/* CONTEÚDO */}
                <main style={{ position: 'relative', zIndex: 1, maxWidth: '1280px', margin: '0 auto', padding: '32px 24px 80px' }}>

                    {/* Page header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg, rgba(37,99,235,0.15), rgba(45,184,225,0.15))', border: '1px solid rgba(37,99,235,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb' }}>
                            <CalendarIcon />
                        </div>
                        <div>
                            <h1 style={{ margin: 0, fontSize: 'clamp(20px,3vw,28px)', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>Revisão do Programa</h1>
                            <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>Confira todos os detalhes antes de iniciar o evento</p>
                        </div>
                    </div>

                    {/* Info card */}
                    <div style={{ background: 'rgba(37,99,235,0.04)', border: '1px solid rgba(37,99,235,0.12)', borderRadius: '16px', padding: '28px', marginBottom: '24px' }}>
                        <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: '100px', background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.18)', color: '#2563eb', fontSize: '11px', fontWeight: 700, marginBottom: '14px' }}>
                            PRÓXIMO EVENTO AGENDADO
                        </span>
                        <h2 style={{ margin: '0 0 6px', fontSize: 'clamp(22px,3vw,30px)', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>{evento.titulo}</h2>
                        {evento.descricao && <p style={{ margin: '0 0 24px', fontSize: '14px', color: '#64748b' }}>{evento.descricao}</p>}

                        <div className="erp-info-grid">
                            {[
                                { icon: <CalendarIcon />, color: '#2563eb', label: 'Data do Evento', value: formatarDataCompleta(evento.data) },
                                { icon: <ClockIcon />, color: '#2CCB90', label: 'Horário Previsto', value: `${info.primeiraHora} às ${info.ultimaHora}` },
                                { icon: <ClockIcon />, color: '#7c3aed', label: 'Duração Estimada', value: formatarTempo(info.tempoProgramadoTotal) },
                                { icon: <CheckIcon />, color: '#ea580c', label: 'Total de Tarefas', value: `${info.totalTarefas} tarefas programadas` },
                            ].map((item, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                    <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: `${item.color}15`, border: `1px solid ${item.color}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: item.color, flexShrink: 0, marginTop: '2px' }}>
                                        {item.icon}
                                    </div>
                                    <div>
                                        <p style={{ margin: '0 0 3px', fontSize: '12px', color: '#94a3b8', fontWeight: 500 }}>{item.label}</p>
                                        <p style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>{item.value}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Stat cards */}
                    <div className="erp-stats-grid" style={{ marginBottom: '24px' }}>
                        {STATS.map((s, i) => (
                            <div key={i} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: '14px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                                <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#64748b', fontWeight: 500 }}>{s.label}</p>
                                <p style={{ margin: '0 0 4px', fontSize: '28px', fontWeight: 800, color: s.color, letterSpacing: '-0.02em' }}>{s.value}</p>
                                <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>{s.sub}</p>
                            </div>
                        ))}
                    </div>

                    {/* Cronograma */}
                    <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '28px', marginBottom: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                            <div style={{ color: '#7c3aed' }}><ListIcon /></div>
                            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>Cronograma Detalhado</h2>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            {evento.tarefas?.map((tarefa, idx) => (
                                <div key={tarefa.id} className="erp-task-card">
                                    {/* Header tarefa */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                            <span style={{ padding: '4px 12px', borderRadius: '8px', background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.18)', color: '#7c3aed', fontSize: '13px', fontWeight: 700 }}>
                                                #{idx + 1}
                                            </span>
                                            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>{tarefa.titulo}</h3>
                                        </div>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 12px', borderRadius: '8px', background: 'rgba(45,184,225,0.08)', border: '1px solid rgba(45,184,225,0.15)', color: '#2DB8E1', fontSize: '12px', fontWeight: 600, flexShrink: 0 }}>
                                            <ClockIcon /> {tarefa.duracao} min
                                        </span>
                                    </div>

                                    {/* Horário */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: '#eff6ff', border: '1px solid #93c5fd', borderLeft: '3px solid #2563eb', borderRadius: '8px', marginBottom: '10px' }}>
                                        <span style={{ color: '#2563eb' }}><ClockIcon /></span>
                                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#2563eb' }}>
                                            Programado: {tarefa.horaInicio} - {tarefa.horaFim}
                                        </span>
                                    </div>

                                    {/* Responsável / Local */}
                                    {(tarefa.responsavel || tarefa.local) && (
                                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '10px' }}>
                                            {tarefa.responsavel && (
                                                <span style={{ fontSize: '13px', color: '#64748b' }}>
                                                    <strong style={{ color: '#1e293b' }}>Responsável:</strong> {tarefa.responsavel}
                                                </span>
                                            )}
                                            {tarefa.local && (
                                                <span style={{ fontSize: '13px', color: '#64748b' }}>
                                                    <strong style={{ color: '#1e293b' }}>Local:</strong> {tarefa.local}
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {/* Descrição texto */}
                                    {(tarefa.descricao_tipo === 'texto' || (!tarefa.descricao_tipo && tarefa.descricao)) && tarefa.descricao && (
                                        <div style={{ padding: '12px 14px', background: 'rgba(139,92,246,0.05)', border: '1px solid rgba(139,92,246,0.12)', borderLeft: '3px solid #a78bfa', borderRadius: '8px' }}>
                                            <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Descrição</p>
                                            <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>{tarefa.descricao}</p>
                                        </div>
                                    )}

                                    {/* Descrição lista */}
                                    {tarefa.descricao_tipo === 'lista' && tarefa.descricao_items?.length > 0 && (
                                        <div style={{ padding: '12px 14px', background: 'rgba(44,203,144,0.05)', border: '1px solid rgba(44,203,144,0.12)', borderLeft: '3px solid #2CCB90', borderRadius: '8px' }}>
                                            <p style={{ margin: '0 0 10px', fontSize: '11px', fontWeight: 700, color: '#2CCB90', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Itens da Tarefa</p>
                                            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                {tarefa.descricao_items.map((item, i) => (
                                                    <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', color: '#64748b' }}>
                                                        <span style={{ color: '#2CCB90', marginTop: '1px', flexShrink: 0 }}><CheckIcon /></span>
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Ready banner */}
                    <div style={{ background: 'rgba(44,203,144,0.05)', border: '1px solid rgba(44,203,144,0.18)', borderRadius: '16px', padding: '28px', textAlign: 'center', marginBottom: '32px' }}>
                        <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(44,203,144,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: '#2CCB90' }}>
                            <CheckIcon />
                        </div>
                        <h3 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 800, color: '#2CCB90' }}>Tudo Pronto para Começar!</h3>
                        <p style={{ margin: '0 0 6px', fontSize: '14px', color: '#64748b', maxWidth: '560px', marginLeft: 'auto', marginRight: 'auto' }}>
                            O evento está programado com <strong style={{ color: '#0f172a' }}>{info.totalTarefas} tarefa{info.totalTarefas !== 1 ? 's' : ''}</strong> e duração total estimada de <strong style={{ color: '#0f172a' }}>{formatarTempo(info.tempoProgramadoTotal)}</strong>.
                        </p>
                        <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8' }}>
                            Quando você iniciar, a primeira tarefa começará automaticamente e o cronômetro será ativado.
                        </p>
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                            onClick={() => navigate('/eventos/diretor')}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '13px 28px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', minWidth: '160px', justifyContent: 'center' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f0fdf8'}
                            onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}
                        >
                            <ArrowLeftIcon /> Voltar
                        </button>
                        <button
                            onClick={iniciarEvento}
                            disabled={processando}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '13px 28px', borderRadius: '12px', background: processando ? 'rgba(44,203,144,0.3)' : 'linear-gradient(135deg, #2CCB90, #22a876)', border: 'none', color: processando ? 'rgba(44,203,144,0.6)' : '#fff', fontSize: '14px', fontWeight: 700, cursor: processando ? 'not-allowed' : 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', minWidth: '200px', justifyContent: 'center', boxShadow: processando ? 'none' : '0 4px 20px rgba(44,203,144,0.25)' }}
                        >
                            {processando ? (
                                <>
                                    <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid rgba(44,203,144,0.3)', borderTopColor: '#2CCB90', animation: 'erp-spin 0.8s linear infinite' }} />
                                    Iniciando...
                                </>
                            ) : (
                                <><PlayIcon /> Iniciar Evento Agora</>
                            )}
                        </button>
                    </div>
                </main>
            </div>
        </>
    );
};

export default EventoRevisaoPage;
